import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Minus, Plus, Check, Users, Utensils, X } from "lucide-react";
import { toast } from "sonner";
import { api, friendlyError } from "@/lib/api";
import { Brand } from "@/components/Nav";
import { DISH_CATEGORIES, DIETARY_OPTIONS, formatPhone } from "@/lib/constants";
import { useEvent } from "@/hooks/useEvent";
import { Confirmation } from "@/components/Confirmation";

const variants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

function Progress({ value }) {
  return (
    <div className="h-1 w-full bg-hair rounded-full overflow-hidden">
      <motion.div className="h-full gold-gradient" animate={{ width: `${value}%` }} transition={{ duration: 0.4 }} />
    </div>
  );
}

const PrimaryBtn = ({ children, disabled, onClick, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className="w-full gold-gradient text-espressoDeep font-bold text-lg py-4 rounded-full shadow-gold disabled:opacity-40 disabled:shadow-none hover:brightness-105 active:scale-[0.98] transition flex items-center justify-center gap-2">
    {children}
  </button>
);

export default function Rsvp() {
  const nav = useNavigate();
  const { data: event } = useEvent();
  const [step, setStep] = useState(0);
  const [duplicate, setDuplicate] = useState(false);
  const [dishDup, setDishDup] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({
    status: "attending", primary_name: "", phone: "", party_size: 1, guests: [],
    bringing_dish: null, dish: { category: "", name: "" }, dietary: [], dietary_notes: "",
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // step flow builder based on choices
  const flow = ["attend"];
  if (form.status === "attending") {
    flow.push("name", "phone", "party");
    if (form.party_size > 1 && event?.allow_additional_guests) flow.push("guests");
    if (event?.dishes_enabled) {
      flow.push("bring");
      if (form.bringing_dish) flow.push("dishcat", "dishname");
    }
    if (event?.dietary_enabled) flow.push("dietary");
    flow.push("review");
  } else {
    flow.push("name", "phone", "review");
  }
  const current = flow[step];
  const progress = ((step + 1) / flow.length) * 100;

  const next = () => setStep((s) => Math.min(s + 1, flow.length - 1));
  const back = () => (step === 0 ? nav("/") : setStep((s) => s - 1));

  const goPhoneNext = async () => {
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length !== 10) return toast.error("Please enter a valid 10-digit mobile number.");
    try {
      const res = await api.post("/rsvp/check-phone", { phone: form.phone });
      if (res.data.exists) { setDuplicate(true); return; }
      next();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  const checkDish = async () => {
    if (!form.dish.name.trim()) return toast.error("What are you bringing?");
    try {
      const res = await api.post("/dish-check", { name: form.dish.name });
      if (res.data.duplicate) { setDishDup(res.data); return; }
      next();
    } catch { next(); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        primary_name: form.primary_name, phone: form.phone, status: form.status,
        party_size: form.party_size, guests: form.guests.filter((g) => g.trim()),
        bringing_dish: !!form.bringing_dish,
        dish: form.bringing_dish && form.dish.name ? form.dish : null,
        dietary: form.dietary, dietary_notes: form.dietary_notes, sms_consent: true,
      };
      const res = await api.post("/rsvp", payload);
      setConfirmed(res.data);
    } catch (e) {
      toast.error(friendlyError(e, "Your RSVP hasn't been saved. Please try again."));
    } finally { setSubmitting(false); }
  };

  if (confirmed) return <Confirmation rsvp={confirmed} event={event} />;

  if (duplicate) {
    return (
      <ShellBare>
        <div className="text-center animate-scale-in">
          <div className="text-5xl mb-4">👋</div>
          <h1 className="font-serif text-3xl text-espresso">Looks like you already RSVP'd!</h1>
          <p className="text-inkSoft mt-3">Want to check or update your plans?</p>
          <div className="mt-8 space-y-3">
            <PrimaryBtn onClick={() => nav("/my-rsvp")} testid="dup-view-rsvp">View My RSVP</PrimaryBtn>
            <button onClick={() => { setDuplicate(false); set({ phone: "" }); }} className="w-full py-3 text-inkSoft font-medium hover:text-espresso" data-testid="dup-try-again">
              Try another number
            </button>
          </div>
        </div>
      </ShellBare>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <div className="sticky top-0 z-40 bg-cream/90 backdrop-blur-xl border-b border-hair">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={back} className="text-espresso hover:text-gold transition p-1" data-testid="rsvp-back"><ArrowLeft size={22} /></button>
          <div className="flex-1"><Progress value={progress} /></div>
          <button onClick={() => nav("/")} className="text-inkMuted hover:text-espresso p-1" data-testid="rsvp-close"><X size={20} /></button>
        </div>
      </div>

      <div className="flex-1 max-w-lg w-full mx-auto px-5 py-8 sm:py-12">
        <AnimatePresence mode="wait">
          <motion.div key={current} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>

            {current === "attend" && (
              <div>
                <p className="text-eyebrow text-gold text-center">Mike & Sam's Friendsgiving</p>
                <h1 className="font-serif text-3xl sm:text-4xl text-espresso text-center mt-3">Will you be joining us for Friendsgiving?</h1>
                <div className="mt-10 space-y-4">
                  <button onClick={() => { set({ status: "attending" }); next(); }} data-testid="attend-yes"
                    className="w-full rounded-3xl gold-gradient text-espressoDeep p-6 text-left shadow-gold hover:brightness-105 active:scale-[0.98] transition">
                    <span className="text-2xl font-serif font-bold">I'm Coming 🥂</span>
                    <p className="text-espressoDeep/70 mt-1 font-medium">Can't wait — count me in!</p>
                  </button>
                  <button onClick={() => { set({ status: "declined" }); setStep(1); }} data-testid="attend-no"
                    className="w-full rounded-3xl bg-white border border-hair p-6 text-left hover:border-espresso active:scale-[0.98] transition">
                    <span className="text-2xl font-serif text-espresso">Can't Make It</span>
                    <p className="text-inkSoft mt-1">We'll miss you this year.</p>
                  </button>
                </div>
              </div>
            )}

            {current === "name" && (
              <StepBody title="What should we call you?" subtitle="First & last name.">
                <input autoFocus value={form.primary_name} onChange={(e) => set({ primary_name: e.target.value })}
                  placeholder="Marcus Johnson" data-testid="input-name"
                  className="w-full bg-white border border-hair rounded-2xl px-5 py-4 text-lg text-espresso focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition" />
                <div className="mt-8"><PrimaryBtn disabled={!form.primary_name.trim()} onClick={next} testid="name-next">Continue <ArrowRight size={20} /></PrimaryBtn></div>
              </StepBody>
            )}

            {current === "phone" && (
              <StepBody title="What's your mobile number?" subtitle="We'll use your number to save your RSVP and help you securely access or update it later. No email or password needed.">
                <input autoFocus inputMode="tel" value={form.phone} onChange={(e) => set({ phone: formatPhone(e.target.value) })}
                  placeholder="(561) 555-1234" data-testid="input-phone"
                  className="w-full bg-white border border-hair rounded-2xl px-5 py-4 text-lg text-espresso focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition" />
                <div className="mt-8">
                  <PrimaryBtn disabled={form.phone.replace(/\D/g, "").length !== 10} onClick={form.status === "declined" ? next : goPhoneNext} testid="phone-next">
                    Continue <ArrowRight size={20} />
                  </PrimaryBtn>
                </div>
              </StepBody>
            )}

            {current === "party" && (
              <StepBody title="How many people will be attending?" subtitle="Total party size — include yourself in this number.">
                <div className="flex items-center justify-center gap-8 my-8">
                  <button onClick={() => set({ party_size: Math.max(1, form.party_size - 1), guests: form.guests.slice(0, Math.max(0, form.party_size - 2)) })}
                    className="w-16 h-16 rounded-full bg-white border border-hair grid place-items-center text-espresso hover:border-gold active:scale-90 transition disabled:opacity-30"
                    disabled={form.party_size <= 1} data-testid="party-minus"><Minus size={26} /></button>
                  <div className="text-center">
                    <span className="font-serif text-7xl text-espresso tabular-nums" data-testid="party-count">{form.party_size}</span>
                    <p className="text-eyebrow text-inkMuted mt-1">{form.party_size === 1 ? "Guest" : "Guests"}</p>
                  </div>
                  <button onClick={() => set({ party_size: Math.min(event?.max_party_size || 8, form.party_size + 1) })}
                    className="w-16 h-16 rounded-full gold-gradient grid place-items-center text-espressoDeep active:scale-90 transition disabled:opacity-30"
                    disabled={form.party_size >= (event?.max_party_size || 8)} data-testid="party-plus"><Plus size={26} /></button>
                </div>
                <PrimaryBtn onClick={next} testid="party-next">Continue <ArrowRight size={20} /></PrimaryBtn>
              </StepBody>
            )}

            {current === "guests" && (
              <StepBody title="Who's coming with you?" subtitle={event?.guest_names_required ? "Please add your guests' names." : "Add names now, or leave blank — totally optional."}>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 rounded-2xl bg-goldLight border border-gold/30 px-5 py-4">
                    <Users size={18} className="text-gold" />
                    <div><p className="text-eyebrow text-inkMuted">You</p><p className="font-semibold text-espresso">{form.primary_name}</p></div>
                  </div>
                  {Array.from({ length: form.party_size - 1 }).map((_, i) => (
                    <input key={i} value={form.guests[i] || ""} placeholder={`Guest ${i + 2} name`} data-testid={`guest-input-${i}`}
                      onChange={(e) => { const g = [...form.guests]; g[i] = e.target.value; set({ guests: g }); }}
                      className="w-full bg-white border border-hair rounded-2xl px-5 py-4 text-espresso focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition" />
                  ))}
                </div>
                <div className="mt-8"><PrimaryBtn
                  disabled={event?.guest_names_required && form.guests.slice(0, form.party_size - 1).some((g) => !g?.trim())}
                  onClick={next} testid="guests-next">Continue <ArrowRight size={20} /></PrimaryBtn></div>
              </StepBody>
            )}

            {current === "bring" && (
              <StepBody title="Want to bring something to the table?" subtitle="Bringing a dish is completely optional.">
                <div className="space-y-4">
                  <button onClick={() => { set({ bringing_dish: true }); next(); }} data-testid="bring-yes"
                    className="w-full rounded-3xl gold-gradient text-espressoDeep p-6 text-left shadow-gold active:scale-[0.98] transition flex items-center gap-4">
                    <Utensils /> <span className="text-xl font-serif font-bold">Yes, I'll bring something</span>
                  </button>
                  <button onClick={() => { set({ bringing_dish: false }); next(); }} data-testid="bring-no"
                    className="w-full rounded-3xl bg-white border border-hair p-6 text-left hover:border-espresso active:scale-[0.98] transition">
                    <span className="text-xl font-serif text-espresso">Not this time</span>
                  </button>
                </div>
              </StepBody>
            )}

            {current === "dishcat" && (
              <StepBody title="What kind of dish are you thinking?" subtitle="Pick a category.">
                <div className="grid grid-cols-2 gap-3">
                  {DISH_CATEGORIES.map((c) => (
                    <button key={c.id} onClick={() => { set({ dish: { ...form.dish, category: c.id } }); next(); }} data-testid={`dishcat-${c.id.replace(/[^a-zA-Z]/g, "")}`}
                      className={`rounded-2xl border p-5 text-center transition active:scale-95 ${form.dish.category === c.id ? "border-gold bg-goldLight" : "border-hair bg-white hover:border-gold"}`}>
                      <div className="text-3xl mb-2">{c.icon}</div>
                      <div className="text-sm font-semibold text-espresso">{c.label}</div>
                    </button>
                  ))}
                </div>
              </StepBody>
            )}

            {current === "dishname" && (
              <StepBody title="What are you bringing?" subtitle="Give it a name so we know what to expect.">
                <input autoFocus value={form.dish.name} onChange={(e) => set({ dish: { ...form.dish, name: e.target.value } })}
                  placeholder="Baked Mac & Cheese" data-testid="input-dish"
                  className="w-full bg-white border border-hair rounded-2xl px-5 py-4 text-lg text-espresso focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition" />
                <TableHint />
                <div className="mt-8"><PrimaryBtn disabled={!form.dish.name.trim()} onClick={checkDish} testid="dish-next">Continue <ArrowRight size={20} /></PrimaryBtn></div>
              </StepBody>
            )}

            {current === "dietary" && (
              <StepBody title="Anything we should know about food?" subtitle="Optional — helps us plan the menu.">
                <div className="flex flex-wrap gap-3">
                  {DIETARY_OPTIONS.map((d) => {
                    const active = form.dietary.includes(d);
                    return (
                      <button key={d} data-testid={`dietary-${d.replace(/[^a-zA-Z]/g, "")}`}
                        onClick={() => set({ dietary: d === "None" ? (active ? [] : ["None"]) : active ? form.dietary.filter((x) => x !== d) : [...form.dietary.filter((x) => x !== "None"), d] })}
                        className={`px-5 py-3 rounded-full border font-medium transition active:scale-95 ${active ? "gold-gradient text-espressoDeep border-transparent" : "bg-white border-hair text-espresso hover:border-gold"}`}>
                        {d}
                      </button>
                    );
                  })}
                </div>
                {(form.dietary.includes("Food Allergy") || form.dietary.includes("Other")) && (
                  <textarea value={form.dietary_notes} onChange={(e) => set({ dietary_notes: e.target.value })} placeholder="Tell us more…" data-testid="input-dietary-notes"
                    className="w-full mt-4 bg-white border border-hair rounded-2xl px-5 py-4 text-espresso focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition" rows={3} />
                )}
                <div className="mt-8"><PrimaryBtn onClick={next} testid="dietary-next">Continue <ArrowRight size={20} /></PrimaryBtn></div>
              </StepBody>
            )}

            {current === "review" && (
              <div>
                <h1 className="font-serif text-3xl text-espresso text-center">Let's make sure everything looks right</h1>
                <div className="mt-8 rounded-3xl bg-white border border-hair shadow-soft divide-y divide-hair" data-testid="review-card">
                  <Row label="Name" value={form.primary_name} />
                  <Row label="Status" value={form.status === "attending" ? "Coming 🥂" : "Can't make it"} />
                  {form.status === "attending" && <>
                    <Row label="Party Size" value={String(form.party_size)} />
                    {form.guests.filter((g) => g.trim()).length > 0 && <Row label="Guests" value={form.guests.filter((g) => g.trim()).join(", ")} />}
                    <Row label="Bringing" value={form.bringing_dish && form.dish.name ? `${form.dish.name} (${form.dish.category})` : "Nothing this time"} />
                    <Row label="Dietary Notes" value={form.dietary.length ? [...form.dietary, form.dietary_notes].filter(Boolean).join(", ") : "None"} />
                  </>}
                </div>
                <div className="mt-8 space-y-3">
                  <PrimaryBtn onClick={submit} disabled={submitting} testid="confirm-rsvp">{submitting ? "Saving…" : "Confirm RSVP"} <Check size={20} /></PrimaryBtn>
                  <button onClick={() => setStep(1)} className="w-full py-3 text-inkSoft font-medium hover:text-espresso" data-testid="make-changes">Make Changes</button>
                </div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* dish duplicate modal */}
      <AnimatePresence>
        {dishDup && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center bg-espressoDeep/60 backdrop-blur-sm p-6">
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-cream rounded-3xl p-8 max-w-sm w-full text-center" data-testid="dish-dup-modal">
              <div className="text-4xl mb-3">🤝</div>
              <h3 className="font-serif text-2xl text-espresso">Great minds think alike!</h3>
              <p className="text-inkSoft mt-2">{dishDup.by} is already bringing <span className="font-semibold text-espresso">{dishDup.name}</span>.</p>
              <div className="mt-6 space-y-3">
                <PrimaryBtn onClick={() => { setDishDup(null); next(); }} testid="dish-bring-anyway">Bring It Anyway</PrimaryBtn>
                <button onClick={() => setDishDup(null)} className="w-full py-3 text-inkSoft font-medium hover:text-espresso" data-testid="dish-choose-else">Choose Something Else</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function StepBody({ title, subtitle, children }) {
  return (
    <div>
      <h1 className="font-serif text-3xl sm:text-4xl text-espresso">{title}</h1>
      {subtitle && <p className="text-inkSoft mt-3 leading-relaxed">{subtitle}</p>}
      <div className="mt-8">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 px-6 py-4">
      <span className="text-eyebrow text-inkMuted pt-1">{label}</span>
      <span className="text-espresso font-medium text-right">{value}</span>
    </div>
  );
}

function TableHint() {
  const { data: event } = useEvent();
  const { data } = useQuery({
    queryKey: ["table-hint"],
    queryFn: async () => (await api.get("/table")).data,
    enabled: !!event?.dish_list_visible,
    retry: false,
  });
  if (!event?.dish_list_visible) return null;
  const cats = data?.categories || {};
  const entries = Object.entries(cats);
  if (entries.length === 0) return null;
  return (
    <div className="mt-4 rounded-2xl bg-cream2 border border-hair p-4" data-testid="table-hint">
      <p className="text-eyebrow text-inkMuted mb-2">What's already on the table</p>
      <div className="flex flex-wrap gap-2">
        {entries.map(([k, v]) => <span key={k} className="text-sm bg-white border border-hair rounded-full px-3 py-1 text-espresso">{k} — {v}</span>)}
      </div>
    </div>
  );
}

function ShellBare({ children }) {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-cream">
      <div className="border-b border-hair bg-cream/90 backdrop-blur-xl">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <Brand />
          <button onClick={() => nav("/")} className="text-inkMuted hover:text-espresso"><X size={20} /></button>
        </div>
      </div>
      <div className="max-w-lg mx-auto px-5 py-16">{children}</div>
    </div>
  );
}
